<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading…</div>
    <div v-if="loaded" class="root">

      <!-- Top band: nav -->
      <nav class="nav">
        <img :src="lightLogoUrl" class="nav-logo" />
        <div class="nav-center">
          <span class="nav-lo">{{ loName || "Loan Officer" }}</span>
          <span class="nav-x">×</span>
          <span class="nav-re">{{ realtorName || realtor?.partner_title || 'Partner' }}</span>
        </div>
       
      </nav>

      <!-- Band 1: Hero copy -->
      <section class="band band-hero">
        <div class="band-inner">
          <div class="hero-left">
        
            <h1 class="hero-h1" v-html="heroH1Html"></h1>
            <p class="hero-body">{{ heroBody }}</p>
            <div class="hero-btns">
              <a class="btn-solid" :href="applyRightLink">{{ heroPrimaryText }}</a>
              <a v-if="applyLeftLink" class="btn-line" :href="applyLeftLink">{{ heroSecondaryText }}</a>
            </div>
          </div>
          <div class="hero-right">
            <div class="quick-card">
              <p class="qc-label">Quick links</p>
              <div class="qc-links">
                <a v-if="loinfo && loinfo.phone_number" class="qc-item" :href="'tel:' + loinfo.phone_number">
                  <span class="qc-icon">↗</span> Call {{ loName || "us" }}
                </a>
                <a v-if="loinfo && loinfo.email" class="qc-item" :href="'mailto:' + loinfo.email">
                  <span class="qc-icon">↗</span> Email {{ loName || "us" }}
                </a>
                <a v-if="realtor && realtor.website_url" class="qc-item" :href="realtor.website_url" target="_blank" rel="noreferrer">
                  <span class="qc-icon">↗</span> Partner site
                </a>
              </div>
              <div class="qc-nmls" v-if="loinfo && loinfo.nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Band 2: Side-by-side profiles -->
      <section class="band band-profiles">
        <!-- LO panel -->
        <div class="profile-panel lo-panel">
          <div class="pp-photo-wrap">
            <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="pp-photo" />
            <div class="pp-photo-placeholder" v-else></div>
          </div>
          <div class="pp-info">
            <span class="pp-tag">Loan Officer</span>
            <div class="pp-name">{{ loName }}</div>
            <div class="pp-role">{{ loTitle }}</div>
            <div v-if="loinfo && loinfo.nmls" class="pp-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
            <div class="pp-contacts">
              <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="pp-chip">{{ loinfo.phone_number }}</a>
              <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="pp-chip">{{ loinfo.email }}</a>
            </div>
           
          </div>
        </div>

        <!-- Center divider -->
        <div class="profile-divider">
          <div class="pd-line"></div>
          <div class="pd-node"></div>
          <div class="pd-line"></div>
        </div>

        <!-- Realtor panel -->
        <div class="profile-panel re-panel">
          <div class="pp-photo-wrap">
            <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="pp-photo" />
            <div class="pp-photo-placeholder" v-else></div>
          </div>
          <div class="pp-info">
            <span class="pp-tag re-tag">{{ realtor?.partner_title || 'Partner' }}</span>
            <div class="pp-name">{{ realtorName }}</div>
            <div class="pp-role">{{ realtor && realtor.brokerage_name ? realtor.brokerage_name : "" }}</div>
            <div v-if="realtor && realtor.nmls" class="pp-nmls">NMLS {{ (realtor.nmls || "").trim() }}</div>
            <div class="pp-contacts">
              <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="pp-chip re-chip">{{ realtor.phone_number }}</a>
              <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="pp-chip re-chip">{{ realtor.email }}</a>
              <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="pp-chip re-chip" target="_blank" rel="noreferrer">Website →</a>
            </div>
          </div>
        </div>
      </section>

      <!-- Band 3: Footer CTA -->
      <section class="band band-footer">
        <div class="band-inner footer-inner">
          <span class="footer-text">Ready to move forward?</span>
          <a class="btn-solid" :href="applyRightLink">{{ heroPrimaryText }} →</a>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending:        { primary: "#eb610a", accent: "#646363" },
  radiantmortgage:    { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage:     { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam:         { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage:         { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage:     { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial:  { primary: "#0b1726", accent: "#c69c39" },
};
const DBA_BASE = "https://api.goldstarfinancial.com/DBA";
function dbaLogo(brandKey, variant = "primary") { return `${DBA_BASE}/${brandKey}/${variant}.png`; }
function getBrandKey(email) {
  if (!email) return null;
  const d = email.slice(email.indexOf("@") + 1).toLowerCase();
  return d.split(".")[0] || null;
}
export default {
  name: "CoBrandMeridian",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    lightLogoUrl() { return dbaLogo(this.brandKey, "primary"); },
    
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Ready to get<br/><em>started?</em>";
      const parts = h.split(",");
      if (parts.length > 1) {
        const a = parts[0].trim();
        const b = parts.slice(1).join(",").trim();
        return `${a}<br/><em>${b}</em>`;
      }
      return h;
    },
    
    heroHeading() { return this.loinfo?.text_section_heading || "About"; },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      const p = [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim();
      return p || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Gilda+Display&family=Mulish:wght@300;400;500;600;700&display=swap');

.page {
  min-height: 100vh;
  background: #f8f7f4;
  color: #1a1a1a;
  font-family: 'Mulish', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --hairline: rgba(26,26,26,0.10);
  --muted: #6b7280;
  --p-tint: color-mix(in srgb, var(--p) 5%, #f8f7f4);
  --a-tint: color-mix(in srgb, var(--a) 10%, #f8f7f4);
}
.root { display: flex; flex-direction: column; min-height: 100vh; }

/* NAV */
.nav {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 48px;
  border-bottom: 1px solid var(--hairline);
  background: #fff;
}
.nav-logo { width: 160px; max-height:80px; object-fit: contain; }
.nav-center { display: flex; align-items: center; gap: 10px; font-size: 13px; }
.nav-lo { font-weight: 600; color: var(--p); }
.nav-x { color: var(--a); font-size: 16px; }
.nav-re { color: var(--muted); }
.nav-cta {
  font-size: 13px; font-weight: 600; text-decoration: none;
  background: var(--p); color: #fff;
  padding: 10px 22px; border-radius: 8px; transition: opacity 0.2s;
}
.nav-cta:hover { opacity: 0.85; }

/* BANDS */
.band { width: 100%; }
.band-inner { max-width: 1100px; margin: 0 auto; padding: 0 48px; }

/* HERO BAND */
.band-hero {
  background: #fff;
  border-bottom: 1px solid var(--hairline);
  padding: 72px 0;
}
.band-inner { display: flex; align-items: center; gap: 60px; }
.hero-left { flex: 1; }
.hero-right { width: 320px; flex-shrink: 0; }

.hero-kicker { font-size: 11px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase; color: var(--a); margin-bottom: 14px; }
.hero-h1 {
  font-family: 'Gilda Display', serif;
  font-size: clamp(40px, 5vw, 68px);
  font-weight: 400; line-height: 1.08;
  color: var(--p); margin: 0 0 20px;
}
.hero-body { font-size: 15px; line-height: 1.75; color: var(--muted);  margin-bottom: 28px; }
.hero-btns { display: flex; gap: 10px; flex-wrap: wrap;justify-content: center; }
.btn-solid {
  font-size: 14px; font-weight: 600; text-decoration: none;
  background: var(--p); color: #fff;
  padding: 13px 26px; border-radius: 8px;
  transition: opacity 0.2s;
  width:55%
}
.btn-solid:hover { opacity: 0.85; }
.btn-line {
  font-size: 14px; font-weight: 500; text-decoration: none;
  color: var(--p); border: 1.5px solid var(--hairline);
  padding: 13px 26px; border-radius: 8px;
  transition: border-color 0.2s, background 0.2s;
}
.btn-line:hover { border-color: color-mix(in srgb, var(--p) 25%, transparent); background: var(--p-tint); }

/* Quick card */
.quick-card {
  background: var(--p-tint);
  border: 1px solid var(--hairline);
  border-radius: 16px; padding: 24px;
}
.qc-label { font-size: 10px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin-bottom: 14px; }
.qc-links { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
.qc-item { font-size: 13px; font-weight: 500; text-decoration: none; color: var(--p); display: flex; align-items: center; gap: 6px; transition: color 0.18s; }
.qc-item:hover { color: color-mix(in srgb, var(--a) 80%, var(--p)); }
.qc-icon { color: var(--a); font-size: 12px; }
.qc-nmls { font-size: 11px; color: var(--muted); padding-top: 12px; border-top: 1px solid var(--hairline); }

/* PROFILES BAND */
.band-profiles {
  display: flex;
  align-items: stretch;
  min-height: 420px;
}
.profile-panel {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 56px 60px;
}
.lo-panel { background: #fff; justify-content: flex-end; }
.re-panel { background: color-mix(in srgb, var(--a) 5%, #f8f7f4); }

.pp-photo-wrap { flex-shrink: 0; }
.pp-photo {
  width: 110px; height: 110px;
  border-radius: 18px; object-fit: cover;
  box-shadow: 0 8px 28px rgba(0,0,0,0.12);
}
.pp-photo-placeholder {
  width: 110px; height: 110px;
  border-radius: 18px;
  background: color-mix(in srgb, var(--p) 8%, #f8f7f4);
}
.pp-tag {
  display: inline-block; font-size: 9px; font-weight: 700;
  letter-spacing: 0.22em; text-transform: uppercase;
  color: var(--muted); margin-bottom: 8px;
}
.re-tag { color: color-mix(in srgb, var(--a) 80%, var(--p)); }
.pp-name { font-family: 'Gilda Display', serif; font-size: 26px; font-weight: 400; color: var(--p); margin-bottom: 4px; }
.pp-role { font-size: 13px; color: var(--muted); margin-bottom: 3px; }
.pp-nmls { font-size: 11px; color: color-mix(in srgb, var(--muted) 65%, transparent); margin-bottom: 14px; }
.pp-contacts { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; margin-bottom: 16px; }
.pp-chip {
  font-size: 11px; font-weight: 500; text-decoration: none;
  color: var(--p); background: var(--p-tint);
  padding: 5px 12px; border-radius: 50px;
  border: 1px solid var(--hairline); transition: background 0.18s;
}
.pp-chip:hover { background: color-mix(in srgb, var(--p) 10%, #f8f7f4); }
.re-chip { color: color-mix(in srgb, var(--a) 85%, var(--p)); background: var(--a-tint); border-color: color-mix(in srgb, var(--a) 18%, transparent); }
.re-chip:hover { background: color-mix(in srgb, var(--a) 18%, #f8f7f4); }
.pp-btn {
  display: inline-block; text-decoration: none;
  background: var(--p); color: #fff;
  font-size: 13px; font-weight: 600;
  padding: 11px 20px; border-radius: 8px; transition: opacity 0.18s;
}
.pp-btn:hover { opacity: 0.85; }

/* Divider */
.profile-divider {
  width: 1px; flex-shrink: 0;
  display: flex; flex-direction: column; align-items: center;
  background: transparent;
  position: relative;
}
.pd-line { flex: 1; width: 1px; background: var(--hairline); }
.pd-node {
  width: 10px; height: 10px; border-radius: 50%;
  background: var(--a); flex-shrink: 0; margin: 8px 0;
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--a) 20%, transparent);
}

/* FOOTER BAND */
.band-footer {
  background: var(--p);
  padding: 32px 0;
}
.footer-inner {
  display: flex; align-items: center; justify-content: space-between;
}
.footer-text { font-family: 'Gilda Display', serif; font-size: 22px; color: rgba(255,255,255,0.85); }
.band-footer .btn-solid { background: var(--a); color: var(--p); }

@media (max-width: 860px) {
  .band-profiles { flex-direction: column; }
  .lo-panel, .re-panel { justify-content: flex-start; padding: 36px 28px; }
  .profile-divider { width: 100%; height: 1px; flex-direction: row; padding: 0 28px; }
  .pd-line { flex: 1; height: 1px; width: auto; }
  .pd-node { margin: 0 8px; }
  .band-inner { flex-direction: column; gap: 32px; }
  .hero-right { width: 100%; }
  .footer-inner { flex-direction: column; gap: 16px; text-align: center; }
  .nav { padding: 16px 24px; }
  .band-hero { padding: 44px 0; }
}
</style>
